import React, { useEffect, useRef, useState } from "react";
import { Callout, DirectionalHint, FocusZone, FocusZoneDirection } from "@fluentui/react";
import { localize } from "src/l10n";
import { Label, SpintrUser } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import SpintrSearch from "src/ui/components/SpintrList/SpintrSearch";
import SpintrLoader from "src/ui/components/Loader";
import { capitalizeFirstLetter } from "src/utils";
import { IShareTarget } from "./ShareModal.types";
import { getRecentShareTargetsAsync, searchShareTargetsAsync } from "../../api/shareApi";
import { shareTargetConfig } from "./shareTargetConfig";
import ShareTargetItem from "./ShareTargetItem";

interface IProps {
    selectedTarget: IShareTarget;
    onSelect: (target: IShareTarget) => void;
    currentUser: any;
}

const searchPageSize = 20;
const loadMoreThreshold = 60;

// The targets proc doesn't order ties within a type, so pages can overlap.
function appendUnique(existing: IShareTarget[], incoming: IShareTarget[]): IShareTarget[] {
    const seen = new Set(existing.map((target) => target.type + "-" + target.id));

    return existing.concat(incoming.filter((target) => !seen.has(target.type + "-" + target.id)));
}

const ShareTargetDropdown = (props: IProps) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const requestId = useRef(0);
    const lastDismissed = useRef(0);
    const nextSkip = useRef(0);
    const isFetchingMore = useRef(false);

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [results, setResults] = useState<IShareTarget[]>([]);
    const [recent, setRecent] = useState<IShareTarget[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const feedTarget: IShareTarget = {
        type: "feed",
        id: props.currentUser ? props.currentUser.id : 0,
        name: capitalizeFirstLetter(localize("SOCIALA_FLODET")),
    };

    const canTargetDepartments = !!(props.currentUser && (props.currentUser.isAdmin || props.currentUser.isEditor));

    // Load "Senast delat till" once.
    useEffect(() => {
        let cancelled = false;

        getRecentShareTargetsAsync()
            .then((items) => {
                if (!cancelled) {
                    setRecent(items);
                }
            })
            .catch(() => { });

        return () => { cancelled = true; };
    }, []);

    // Debounced realtime search.
    useEffect(() => {
        const currentRequest = ++requestId.current;
        isFetchingMore.current = false;
        setIsLoadingMore(false);
        setHasMore(false);

        if (!searchText) {
            setResults([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const handle = setTimeout(() => {
            searchShareTargetsAsync(searchText, 0, searchPageSize, canTargetDepartments)
                .then((page) => {
                    if (currentRequest === requestId.current) {
                        nextSkip.current = searchPageSize;
                        setResults(appendUnique([], page.items));
                        setHasMore(page.hasMore);
                        setIsLoading(false);
                    }
                })
                .catch(() => {
                    if (currentRequest === requestId.current) {
                        setIsLoading(false);
                    }
                });
        }, 250);

        return () => clearTimeout(handle);
    }, [searchText, canTargetDepartments]);

    const loadMore = () => {
        if (isLoading || !hasMore || isFetchingMore.current) {
            return;
        }

        const currentRequest = requestId.current;
        const skip = nextSkip.current;
        isFetchingMore.current = true;
        setIsLoadingMore(true);

        searchShareTargetsAsync(searchText, skip, searchPageSize, canTargetDepartments)
            .then((page) => {
                if (currentRequest === requestId.current) {
                    nextSkip.current = skip + searchPageSize;
                    setResults((existing) => appendUnique(existing, page.items));
                    setHasMore(page.hasMore);
                }
            })
            .catch(() => { })
            .finally(() => {
                if (currentRequest === requestId.current) {
                    isFetchingMore.current = false;
                    setIsLoadingMore(false);
                }
            });
    };

    // Loads the next page when the list is scrolled (or arrowed) near its bottom.
    const onListScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const list = event.currentTarget;

        if (list.scrollHeight - list.scrollTop - list.clientHeight < loadMoreThreshold) {
            loadMore();
        }
    };

    const toggleOpen = () => {
        // Ignore the click right after a Callout dismiss (field click dismisses, then re-fires).
        if (Date.now() - lastDismissed.current < 300) {
            return;
        }

        setIsOpen((open) => !open);
    };

    const closeDropdown = () => {
        lastDismissed.current = Date.now();
        setIsOpen(false);
    };

    const handleSelect = (target: IShareTarget) => {
        props.onSelect(target);
        setSearchText("");
        closeDropdown();
    };

    const isSelected = (target: IShareTarget) =>
        target.type === props.selectedTarget.type && target.id === props.selectedTarget.id;

    const config = shareTargetConfig[props.selectedTarget.type];
    const isSearching = !!searchText;
    const visibleRecent = canTargetDepartments ? recent : recent.filter((target) => target.type !== "department");

    return (
        <div className="ShareTargetDropdown">
            <div
                className={"ShareTargetDropdown-field" + (props.selectedTarget.type === "feed" ? " is-feed" : "")}
                ref={anchorRef}
                role="button"
                tabIndex={0}
                onClick={toggleOpen}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleOpen();
                    }
                }}
            >
                <SpintrUser
                    name={props.selectedTarget.name}
                    imageUrl={props.selectedTarget.imageUrl}
                    iconToDisplayInsteadOfInitials={!props.selectedTarget.imageUrl ? config.iconName : undefined}
                    personalName={props.selectedTarget.type === "user"}
                />
                <Visage2Icon icon={isOpen ? "arrow-up-2" : "arrow-down-1"} />
            </div>
            {isOpen && (
                <Callout
                    className="ShareTargetDropdown-callout"
                    target={anchorRef}
                    onDismiss={closeDropdown}
                    isBeakVisible={false}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    directionalHintFixed={true}
                    gapSpace={4}
                    calloutWidth={anchorRef.current ? anchorRef.current.offsetWidth : undefined}
                    setInitialFocus={true}
                >
                    {/* Arrow keys move between the search box and the rows. Tab closes the list and
                        returns focus to the field, like a Fluent Dropdown. */}
                    <FocusZone
                        direction={FocusZoneDirection.vertical}
                        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                            if (event.key === "Tab") {
                                event.preventDefault();
                                closeDropdown();
                                anchorRef.current?.focus();
                            }
                        }}
                    >
                        <div className="ShareTargetDropdown-search">
                            <SpintrSearch
                                value={searchText}
                                placeholder={localize("SHARE_SEARCH_PLACEHOLDER")}
                                onClear={() => setSearchText("")}
                                onChange={(_event: React.ChangeEvent, value: string) => setSearchText(value)}
                            />
                        </div>
                        <div className="ShareTargetDropdown-list" onScroll={isSearching ? onListScroll : undefined}>
                            {isSearching ? (
                                <>
                                    {isLoading && <SpintrLoader />}
                                    {!isLoading && results.length === 0 && (
                                        <Label className="ShareTargetDropdown-empty" size="body-2" color="mid-grey">
                                            {localize("IngaTraffar")}
                                        </Label>
                                    )}
                                    {results.map((target) => (
                                        <ShareTargetItem
                                            key={target.type + "-" + target.id}
                                            target={target}
                                            onSelect={handleSelect}
                                            isSelected={isSelected(target)}
                                        />
                                    ))}
                                    {isLoadingMore && <SpintrLoader loaderSize="small" />}
                                </>
                            ) : (
                                <>
                                    <ShareTargetItem
                                        key="feed"
                                        target={feedTarget}
                                        onSelect={handleSelect}
                                        isSelected={isSelected(feedTarget)}
                                    />
                                    {visibleRecent.length > 0 && (
                                        <div className="ShareTargetDropdown-recent">
                                            <Label className="ShareTargetDropdown-section-title" size="body-2" color="mid-grey">
                                                {localize("SHARE_RECENT_TITLE")}
                                            </Label>
                                            {visibleRecent.map((target) => (
                                                <ShareTargetItem
                                                    key={"recent-" + target.type + "-" + target.id}
                                                    target={target}
                                                    onSelect={handleSelect}
                                                    isSelected={isSelected(target)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </FocusZone>
                </Callout>
            )}
        </div>
    );
};

export default ShareTargetDropdown;
